import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import cn from 'classnames';
import nanoid from 'nanoid';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';

import { setDocumentTitleBase } from '../../actions/actions';
import { focusChatTextArea, getRoomName } from '../../utils/utilsChat';
import {
  flashElement,
  scrollToElement,
  setToLocalStorage,
  exposeToGlobalScope,
  exposeComponentToGlobalScope,
  shortenFileName,
  prefixPrevProps,
  inflect,
} from '../../utils/utilsGlobal';

import services, { events } from '../../services/services';
import TypingUsersSubscription from '../../services/TypingUsersSubscription';
import UpdateMessageSubscription from '../../services/UpdateMessageSubscription';
import {
  closeCase,
  createTaskInRoom,
  linkJiraTaskWithRoom,
  requestScreenshot,
  updateRoom
} from '../../services/apiChat';

import {
  ChatMessages,
  ChatTextArea,
  CreateTaskModal,
  CaseCloseModal,
  Tooltip,
  Modal,
  MessageSearch
} from '../../components';

import styles from './Chat.scss';
import uiStyles from '../../styles/ui.scss';


const mapStateToProps = state => {
  return {
    messages: state.messages,
    currentRoom: state.currentRoom,
    currentCompany: state.currentCompany,
    currentOperator: state.currentOperator,
  };
};

const mapDispatchToProps = { setDocumentTitleBase };

@connect(mapStateToProps, mapDispatchToProps)
@withRouter
export class Chat extends Component {

  DEFAULT_MESSAGES_PER_PAGE = 20;

  state = {
    isLoading: true,
    isLoadingPreviousMessages: false,
    isCreateTaskModalOpen: false,
    isCloseCaseModalOpen: false,
    isCurrentRoomCaseOpen: true,
    isFullScreenPreviewOpen: false,
    isEditingRoomName: false,
    isSubmittingRoomName: false,
    text: '',
    attachments: [],
    responseToMessageId: null,
    mentionedUserIds: [],
    mustOpenWidget: false,
    editingOriginalMessage: null,
    editingChangedFields: {},
    currentlyTypingUsers: [],
    roomNameText: '',
    // Search
    searchFormShow: false,
    searchText: '',
    searchMessagesIds: [], // id сообщений, которые совпадают с текстом поиска
    selectMessageId: null, // id сообщения, которое необходимо выделить
    showScrollButton: false
  };

  scrollBlock = React.createRef();
  chatMessages = React.createRef();
  editRoomNameTextarea = React.createRef();
  chatMessagesWrapper = React.createRef();

  updateMessageSubscription = null;
  typingUsersSubscription = null;

  componentDidMount(){
    const { currentRoom } = this.props;
    this.setState({ isCurrentRoomCaseOpen: currentRoom.isCaseOpen });

    this.initializeServices();
    this.onRoomChange(currentRoom);

    exposeComponentToGlobalScope(this);
    document.addEventListener('keydown', this.onDocumentKeyDown);
  
    /**
     * Открытие окна поиска при нажатии на cmd|ctrl + f
     * @param event
     */
    window.onkeydown = (event) => {
      const letters = ['f', 'а'];
      if (letters.includes(event.key) && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        this.setState({ searchFormShow: true })
      }
    }
  
  }

  componentDidUpdate(prevProps){
    const { currentRoom } = this.props;
    const { prevCurrentRoom } = prefixPrevProps(prevProps);

    if (currentRoom.id !== prevCurrentRoom.id) {
      this.onRoomChange(currentRoom);
    }
    if (currentRoom.isCaseOpen !== prevCurrentRoom.isCaseOpen) {
      this.setState({ isCurrentRoomCaseOpen: currentRoom.isCaseOpen });
    }
  }

  componentWillUnmount(){
    this.typingUsersSubscription.unsubscribeFromCurrentRoom();
    this.updateMessageSubscription.unsubscribeFromCurrentRoom();
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  initializeServices = () => {
    services.messageSubscription.on(events.MESSAGES_RECEIVE, this.onMessageReceive);
    services.messageSubscription.on(events.MESSAGES_CHANGE, this.onMessagesChange);

    this.updateMessageSubscription = new UpdateMessageSubscription();
    this.updateMessageSubscription.on(events.UPDATE_MESSAGES_CHANGE, updatedMessage => {
      services.messageSubscription.changeMessageBy({ id: updatedMessage.id }, updatedMessage);
    });
    exposeToGlobalScope('services.updateMessageSubscription', this.updateMessageSubscription);

    this.typingUsersSubscription = new TypingUsersSubscription();
    this.typingUsersSubscription.on(events.TYPING_USERS_CHANGE, this.onTypingUsersChange);
    this.typingUsersSubscription.connect().then(() => {
      exposeToGlobalScope('services.typingUsersSubscription', this.typingUsersSubscription);
    });

    services.userWatcher.on(events.USER_LOGGED_OUT, () => {
      if (this.typingUsersSubscription) {
        this.typingUsersSubscription.destroy();
      }
    });
  };

  onDocumentKeyDown = (e) => {
    const { editingOriginalMessage } = this.state;
    if (e.key === 'Escape' && editingOriginalMessage) {
      this.cancelEditingMessage();
    }
  };

  onRoomChange = (nextRoom) => {
    if (!nextRoom.id || nextRoom.id === 'default') {
      return;
    }
  
    this.closeSearch();

    const { setDocumentTitleBase } = this.props;
    setDocumentTitleBase(getRoomName(nextRoom));
    this.setState({
      currentlyTypingUsers: [],
      roomNameText: nextRoom.title,
    });

    services.messageSubscription.setRoom(nextRoom.id);
    this.typingUsersSubscription.subscribeToRoom(nextRoom.id);
    this.updateMessageSubscription.subscribeToRoom(nextRoom.id);

    this.resetTextareaState();
    this.restoreEnteredMessageFromLocalStorage();
    this.loadLatestMessages().then(this.scrollToBottom);
  };

  onTypingUsersChange = currentlyTypingUsers => {
    this.setState({ currentlyTypingUsers });
  };

  onMessageReceive = message => {
    const { currentRoom } = this.props;
    const isMessageSentToCurrentRoom = message.room.id === currentRoom.id;

    const shouldFlashMessage = isMessageSentToCurrentRoom && message.sender.isOperator && !message.sender.isCurrentOperator;
    const shouldScrollToBottom = isMessageSentToCurrentRoom && (
      message.sender.isCurrentOperator || !message.sender.isCurrentOperator && !this.doMessagesHaveUserScroll()
    );
    if (shouldFlashMessage) {
      requestAnimationFrame(() => {
        flashElement(this.getMessageElement(message.id));
      });
    }
    if (shouldScrollToBottom) {
      setTimeout(this.scrollToBottom);
    }
  };

  onMessagesChange = () => {
    const messagesHaveUserScroll = this.doMessagesHaveUserScroll();
    requestAnimationFrame(() => {
      if (!messagesHaveUserScroll) {
        this.scrollToBottom();
      }
    });
  };

  getMessageElement = (messageId) => {
    return this.chatMessages.current?.messageRefs?.[messageId];
  };

  onMessageSubmit = () => {
    const messagesHaveUserScroll = this.doMessagesHaveUserScroll();
    const {
      text,
      attachments,
      responseToMessageId,
      mentionedUserIds,
      mustOpenWidget,
      editingOriginalMessage,
      editingChangedFields,
    } = this.state;

    const messageData = {
      text: text.trim(),
      responseToMessageId,
      attachments,
      mustOpenWidget,
      mentionedUserIds,
    };

    // Submitting existing edited message
    if (editingOriginalMessage) {
      const didAnythingChange = !isEmpty(editingChangedFields);
      if (didAnythingChange) {
        services.messageSubscription.editMessage(messageData, editingOriginalMessage, editingChangedFields.attachments);
      }
      this.cancelEditingMessage();
    }

    // Submitting new message
    else {
      services.messageSubscription.sendMessage({ ...messageData, appendConditionally: true });
      this.typingUsersSubscription.dispatchTypedText(false);
      this.resetTextareaState();
      this.saveEnteredMessageToLocalStorage({ text: '', responseToMessageId: null });
    }

    if (!messagesHaveUserScroll) {
      setTimeout(this.scrollToBottom);
    }
  };

  resetTextareaState = () => {
    this.setState({
      text: '',
      attachments: [],
      responseToMessageId: null,
      mentionedUserIds: [],
      mustOpenWidget: false,
    });
  };

  scrollToBottom = () => {
    const scrollBlock = this.scrollBlock.current;
    if (scrollBlock) {
      scrollBlock.scrollTop = scrollBlock.scrollHeight;
    }
  };

  // Indicates whether the user has scrolled messages up
  // (meaning that he's probably reading some previously occurred conversation)
  doMessagesHaveUserScroll = () => {
    const scrollBlock = this.scrollBlock.current;
    if (scrollBlock) {
      return scrollBlock.scrollTop <= scrollBlock.scrollHeight - scrollBlock.offsetHeight - 60;
    }
  };

  loadLatestMessages = () => {
    this.setState({ isLoading: true });
    return services.messageSubscription.fetchMessageHistory(this.DEFAULT_MESSAGES_PER_PAGE).finally(() => {
      this.setState({ isLoading: false });
    });
  };

  loadPreviousMessages = () => {
    const { isLoading, isLoadingPreviousMessages } = this.state;
    const scrollBlock = this.scrollBlock.current;
    const initialScrollHeight = scrollBlock.scrollHeight;

    if (!isLoading && !isLoadingPreviousMessages && !services.messageSubscription.reachedBeginningOfMessageHistory) {
      this.setState({ isLoadingPreviousMessages: true });

      services.messageSubscription.fetchPrecedingMessageHistory(this.DEFAULT_MESSAGES_PER_PAGE).finally(() => {
        this.setState({ isLoadingPreviousMessages: false });
        scrollBlock.scrollTop = scrollBlock.scrollHeight - initialScrollHeight;
      });
    }
  };
  
  /**
   * Загрузка новых сообщений
   */
  loadNextMessages = () => {
    const { isLoading } = this.state;
    if (!isLoading && !services.messageSubscription.reachedEndingOfMessageHistory) {
      this.setState({showScrollButton: true});
      services.messageSubscription.fetchNextMessageHistory(this.DEFAULT_MESSAGES_PER_PAGE);
    }
  };

  onReplyTo = message => {
    this.setState({ responseToMessageId: message.id });
    if (this.props.editingOriginalMessage) {
      this.markFieldsEdited(['responseToMessageId']);
    }
  };

  markFieldsEdited = (newFields) => {
    const { editingChangedFields } = this.state;
    const newChangedFields = {};
    newFields.forEach(key => { newChangedFields[key] = true });
    this.setState({
      editingChangedFields: { ...editingChangedFields, ...newChangedFields }
    });
  };

  generateEditingMessageAttachmentPlaceholder = (originalAttachments) => {
    let attachments = [];
    if (originalAttachments.length) {
      const attachmentListMaxLength = 2;
      const attachmentListOverLimitCount = originalAttachments.length - attachmentListMaxLength;
      const attachmentListFileNames = originalAttachments
        .slice(0, attachmentListMaxLength)
        .map(attachment => shortenFileName(attachment.name))
        .join(', ');

      let attachmentListSuffix = '';
      if (attachmentListOverLimitCount > 0) {
        attachmentListSuffix = ` и еще ${inflect(attachmentListOverLimitCount, ['файл', 'файла', 'файлов'])}`
      }
      attachments = [{
        id: nanoid(),
        name: attachmentListFileNames + attachmentListSuffix,
        editingPlaceholder: true,
      }];
    }
    return attachments;
  };

  onStartEditingMessage = message => {
    if (!message?.id) {
      return;
    }
    let responseToMessageId = message.responseToMessage?.id || null;
    if (message.responseToMessage?.isDeleted) {
      responseToMessageId = null;
    }
    this.setState({
      text: message.text,
      responseToMessageId,
      attachments: this.generateEditingMessageAttachmentPlaceholder(message.attachments),
      mentionedUserIds: message.mentions.map(mention => mention.value),
      mustOpenWidget: message.mustOpenWidget,
      editingOriginalMessage: message,
    }, () => {
      setTimeout(() => {
        scrollToElement(this.getMessageElement(message.id));
        focusChatTextArea();
      });
    });
  };

  cancelEditingMessage = () => {
    this.typingUsersSubscription.dispatchTypedText(false);
    this.setState({
      editingOriginalMessage: null,
      editingChangedFields: {},
    });
    this.resetTextareaState();
    this.saveEnteredMessageToLocalStorage({ text: '', responseToMessageId: null });
  };

  onVerticalResize = (chatTextAreaHeight) => {
    const messagesHaveUserScroll = this.doMessagesHaveUserScroll();
    // this.scrollBlock.current.style.bottom = chatTextAreaHeight + 'px';
    if (!messagesHaveUserScroll) {
      this.scrollToBottom();
    }
  };

  onTextareaChange = (diff, callback) => {
    const {
      text,
      responseToMessageId,
      mentionedUserIds,
      mustOpenWidget,
      editingOriginalMessage,
    } = this.state;

    if (editingOriginalMessage) {
      this.markFieldsEdited(Object.keys(diff));
    }
    else {
      this.saveEnteredMessageToLocalStorage({
        text,
        responseToMessageId,
        mentionedUserIds,
        mustOpenWidget,
        ...diff
      });
      this.typingUsersSubscription.dispatchTypedText(diff.text || '');
    }
    this.setState(diff, callback);
  };

  saveEnteredMessageToLocalStorage = (textareaParams) => {
    const { currentRoom } = this.props;
    const {
      text,
      responseToMessageId,
      mentionedUserIds = [],
      mustOpenWidget = false,
    } = textareaParams;

    if (!text && !responseToMessageId && !mentionedUserIds.length && !mustOpenWidget) {
      localStorage.removeItem('TYPED_TEXT_' + currentRoom.id);
    }
    else {
      setToLocalStorage('TYPED_TEXT_' + currentRoom.id, {
        text,
        responseToMessageId,
        mentionedUserIds,
        mustOpenWidget,
      });
    }
  };

  restoreEnteredMessageFromLocalStorage = () => {
    const { currentRoom } = this.props;
    let restoredMessageData;
    try {
      restoredMessageData = JSON.parse(localStorage.getItem('TYPED_TEXT_' + currentRoom.id));
      restoredMessageData = {
        text: restoredMessageData.text || '',
        responseToMessageId: restoredMessageData.responseToMessageId || null,
        mentionedUserIds: restoredMessageData.mentionedUserIds || [],
        mustOpenWidget: restoredMessageData.mustOpenWidget || false,
      }
    }
    catch (e) {
      restoredMessageData = {
        text: '',
        responseToMessageId: null,
        mentionedUserIds: [],
        mustOpenWidget: false,
      };
    }
    this.setState(restoredMessageData);
    this.typingUsersSubscription.dispatchTypedText(restoredMessageData.text);
  };

  onMessagesScroll = () => {
    const scrollBlock = this.scrollBlock.current;
    if (scrollBlock?.scrollTop <= 0) {
      this.loadPreviousMessages();
      return;
    }
    if (scrollBlock?.scrollHeight) {
      let scrollBottom = scrollBlock.scrollHeight - scrollBlock.scrollTop - scrollBlock.clientHeight;
      this.setState({showScrollButton: scrollBottom > 160});
      if (scrollBottom < 1) {
        this.loadNextMessages();
      }
    }
  };

  onTaskSubmit = ({ taskType, comment, jiraTask, timestamp }) => {
    const { currentRoom } = this.props;
    if (taskType === 'link-to-jira') {
      linkJiraTaskWithRoom(currentRoom.id, jiraTask.id).catch(e => {
        if (e.errorType === 'ALREADY_LINKED_WITH_JIRA_TASK') {
          e.overrideErrorNotification({
            errorMessage: 'Этот чат уже связан с задачей ' + jiraTask.title,
          });
        }
      });
    }
    else {
      createTaskInRoom(currentRoom.id, taskType, timestamp.toISOString(), comment);
    }
    this.setState({ isCreateTaskModalOpen: false });
    focusChatTextArea();
  };

  onCloseCaseSubmit = (args) => {
    this.setState({ isCurrentRoomCaseOpen: false });
    return closeCase(this.props.currentRoom.id, [args.caseReasonType.value], args.caseStateOption)
      .then(() => {
        focusChatTextArea();
        this.setState({ isCloseCaseModalOpen: false });
      })
      .catch(() => {
        this.setState({ isCurrentRoomCaseOpen: true });
        focusChatTextArea();
      });
  }

  getLatestEditableMessage = () => {
    const { messages } = this.props;
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message.isEditable && !message.isDeleted) {
        return message;
      }
    }
    return null;
  };

  onEditRoomNameClick = () => {
    this.setState({ isEditingRoomName: true });
    requestAnimationFrame(() => {
      this.editRoomNameTextarea.current.focus();
    });
  };

  onEditRoomNameSubmit = () => {
    const { currentRoom } = this.props;
    const { roomNameText } = this.state;
    this.setState({ isSubmittingRoomName: true });

    return updateRoom(currentRoom.id, { title: roomNameText.trim() })
      .then(updatedRoom => {
        services.roomListSubscription.updateRoomById(currentRoom.id, { title: updatedRoom.title });
        this.setState({
          roomNameText: updatedRoom.title,
          isEditingRoomName: false,
        });
      })
      .finally(() => {
        this.setState({ isSubmittingRoomName: false });
      });
  };

  onEditRoomNameKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.onEditRoomNameSubmit();
    }
  };
  
  /** Search **/
  onToggleSearchForm = () => {
    const state = this.state.searchFormShow;
    
    if (state) {
      this.closeSearch();
    }
    this.setState({ searchFormShow: !state })
  };
  
  /**
   * Прокрутка до нужного сообщения
   * @param messageId
   * @param direction – с какой стороны «прокручивается» текст
   */
  scrollToMessage = (messageId, direction) => {
    const scrollBlock = this.scrollBlock.current;
    const target = document.getElementById(messageId);
    const chatMessages = this.chatMessagesWrapper.current;
    
    this.setState({selectMessageId: messageId});
    if (!target) {
      scrollBlock.scrollTo({top: chatMessages.clientHeight - 20, behavior: 'smooth'});
      return;
    }
    const gap = scrollBlock.clientHeight / 2 - target.clientHeight / 2;
    
    if (direction === 'up') {
      scrollBlock.scrollTo({top: chatMessages.clientHeight - 20, behavior: 'auto'});
    } else if (direction === 'down') {
      scrollBlock.scrollTo({top: 0, behavior: 'auto'});
    }
    
    scrollBlock.scrollTo({top: target.offsetTop - gap, behavior: 'smooth'});
  }
  
  handleScrollToBottom = () => {
    console.log(services.messageSubscription.reachedEndingOfMessageHistory);
    if (!services.messageSubscription.reachedEndingOfMessageHistory) {
      services.messageSubscription.clearReachedState();
      this.loadLatestMessages().then(this.scrollToBottom);
    } else {
      const scrollBlock = this.scrollBlock.current;
      if (scrollBlock) {
        this.setState({showScrollButton: false});
        scrollBlock.scrollTo({top: scrollBlock.scrollHeight, behavior: 'smooth'});
      }
    }
  }
  
  closeSearch = () => {
    this.setState({selectMessageId: null, searchFormShow: false});
    focusChatTextArea();
  }

  render(){
    const {
      className,
      currentRoom,
      messages,
    } = this.props;

    const {
      isLoading,
      isLoadingPreviousMessages,
      isCreateTaskModalOpen,
      isCloseCaseModalOpen,
      isCurrentRoomCaseOpen,
      isEditingRoomName,
      isFullScreenPreviewOpen,
      currentlyTypingUsers,
      text,
      attachments,
      responseToMessageId,
      mentionedUserIds,
      mustOpenWidget,
      editingOriginalMessage,
      isSubmittingRoomName,
      roomNameText,
      searchFormShow,
      searchText,
      searchMessagesIds,
      selectMessageId,
      showScrollButton
    } = this.state;

    const isDefaultRoom = currentRoom.id === 'default';
    const responseToMessage = messages.filter(message => message.id === responseToMessageId)[0];

    return (
      <div className={cn({
        [className]: true,
        [styles.container]: true,
        [styles.containerOverlap]: isFullScreenPreviewOpen || isCreateTaskModalOpen || isEditingRoomName,
      })}>

        {(isDefaultRoom) && (
          <div className={styles.defaultRoomMessage}>Комнаты не найдены</div>
        )}

        {!isDefaultRoom && (
          <Fragment>
            {isCreateTaskModalOpen && (
              <CreateTaskModal
                onSubmit={this.onTaskSubmit}
                onClose={() => this.setState({ isCreateTaskModalOpen: false })}/>
            )}

            {isCloseCaseModalOpen && (
              <CaseCloseModal
                onSubmit={this.onCloseCaseSubmit}
                onClose={() => this.setState({ isCloseCaseModalOpen: false })}/>
            )}

            <div className={styles.header}>
              <h2 className={styles.headerTitle}>
                <span className={styles.headerText}>{getRoomName(currentRoom)}</span>
                <Tooltip title="Переименовать чат">
                  <i className={cn('icon-edit', styles.headerIcon, styles.headerIconEdit)} onClick={this.onEditRoomNameClick}/>
                </Tooltip>
  
                <Tooltip title="Поиск по чату">
                  <i className={cn({
                    'icon-search': true,
                    [styles.headerIcon]: true,
                    [styles.headerIconSearch]: true,
                    [styles.headerIconSearchActive]: searchFormShow,
                  })} onClick={this.onToggleSearchForm}/>
                </Tooltip>
              </h2>
              <MessageSearch
                show={searchFormShow}
                onChangeText={arg => {this.setState({searchText: arg})}}
                onChangeSearchMessagesIds={arg => {this.setState({searchMessagesIds: arg})}}
                onScroll={this.scrollToMessage}
                onClose={this.closeSearch}
                setLoaderState={flag => {this.setState({isLoading: flag})}}
              />
            </div>

            <div ref={this.scrollBlock}
              onScroll={debounce(this.onMessagesScroll, 100)}
              className={cn({
                [styles.messagesContainer]: true,
                [styles.messagesLoading]: isLoading,
              })}>

              {isLoadingPreviousMessages && (
                <div className={styles.loadingPreviousMessages}>Загрузка...</div>
              )}

              {!isLoading && Boolean(messages.length) && (
                <div ref={this.chatMessagesWrapper}>
                  <ChatMessages
                    ref={this.chatMessages}
                    searchText={searchText}
                    searchMessagesIds={searchMessagesIds}
                    selectMessageId={selectMessageId}
                    messages={messages}
                    currentlyTypingUsers={currentlyTypingUsers}
                    editingOriginalMessage={editingOriginalMessage}
                    showScrollButton={showScrollButton}
                    onReplyTo={this.onReplyTo}
                    onScroll={this.handleScrollToBottom}
                    onStartEditingMessage={this.onStartEditingMessage}
                    onToggleFullScreenPreview={(isFullScreenPreviewOpen) => { this.setState({ isFullScreenPreviewOpen }) }}/>
                </div>
              )}

              {!isLoading && Boolean(!messages.length) && (
                <div className={styles.centerPlaceholder}>Пока нет сообщений</div>
              )}
            </div>

            <ChatTextArea className={styles.textareaContainer}
              text={text}
              attachments={attachments}
              responseToMessage={responseToMessage}
              mentionedUserIds={mentionedUserIds}
              mustOpenWidget={mustOpenWidget}
              onChange={this.onTextareaChange}
              onSubmit={this.onMessageSubmit}
              editingOriginalMessage={editingOriginalMessage}
              onStartEditingMessage={() => this.onStartEditingMessage(this.getLatestEditableMessage())}
              onCancelEditingMessage={this.cancelEditingMessage}
              onVerticalResize={this.onVerticalResize}
              onCaseCloseClick={() => this.setState({ isCloseCaseModalOpen: true })}
              onCreateTaskClick={() => this.setState({ isCreateTaskModalOpen: true })}
              onRequestScreenshotClick={() => requestScreenshot(currentRoom.id)}
              isCurrentRoomCaseOpen={isCurrentRoomCaseOpen}
              currentRoom={currentRoom}
            />

            {isEditingRoomName && (
              <Modal
                title="Переименовать чат"
                onClose={() => this.setState({ isEditingRoomName: false })}
                verticallyCentered={true}
                overrideClassNames={{
                  modal: styles.editRoomNameModal
                }}>
                <textarea
                  className={styles.editRoomNameTextarea}
                  ref={this.editRoomNameTextarea}
                  value={roomNameText}
                  onKeyDown={this.onEditRoomNameKeyDown}
                  onChange={e => this.setState({ roomNameText: e.target.value })}/>
                <button className={cn({
                  [uiStyles.buttonPrimary]: true,
                  [styles.editRoomNameButtonSubmitting]: isSubmittingRoomName,
                })} onClick={this.onEditRoomNameSubmit}>
                  Сохранить
                </button>
              </Modal>
            )}

          </Fragment>
        )}
      </div>
    );
  }
}
